import { logEvent } from 'firebase/analytics';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';

/**
 * Service for logging client-side errors to Firebase
 */
class ErrorLoggingService {
  constructor(analytics, db) {
    this.analytics = analytics;
    this.db = db;
    this.isProduction = process.env.NODE_ENV === 'production';
    
    // Rate limiting to prevent spam
    this.errorCounts = new Map();
    this.maxErrorsPerType = 10; // Max errors per type per session
    this.maxTotalErrors = 50; // Max total errors per session
    this.totalErrorCount = 0;
  }

  /**
   * Check if error logging should be rate limited
   */
  shouldRateLimit(errorType) {
    // Check total error limit
    if (this.totalErrorCount >= this.maxTotalErrors) {
      console.warn('Error logging rate limited: total error limit reached');
      return true;
    }

    // Check per-error-type limit
    const typeCount = this.errorCounts.get(errorType) || 0;
    if (typeCount >= this.maxErrorsPerType) {
      console.warn(`Error logging rate limited: ${errorType} limit reached`);
      return true;
    }

    return false;
  }

  /**
   * Increment error counts for rate limiting
   */
  incrementErrorCount(errorType) {
    this.totalErrorCount++;
    this.errorCounts.set(errorType, (this.errorCounts.get(errorType) || 0) + 1);
  }

  /**
   * Get user context for error logging
   */
  getUserContext(currentUser) {
    return {
      userId: currentUser?.uid || 'anonymous',
      email: currentUser?.email || null,
      isAnonymous: !currentUser || currentUser.isAnonymous
    };
  }

  /**
   * Get environment context for error logging
   */
  getEnvironmentContext() {
    return {
      userAgent: navigator.userAgent,
      url: window.location.href,
      timestamp: new Date().toISOString(),
      viewport: `${window.innerWidth}x${window.innerHeight}`,
      platform: navigator.platform
    };
  }

  /**
   * Log error to Firebase Analytics (lightweight, always on)
   */
  async logToAnalytics(errorType, error, userContext = {}, additionalData = {}) {
    if (!this.analytics) {
      console.warn('Firebase Analytics not available for error logging');
      return;
    }

    // Check rate limiting
    if (this.shouldRateLimit(errorType)) {
      return;
    }

    try {
      this.incrementErrorCount(errorType);
      const eventData = {
        error_type: errorType,
        error_message: this.truncateString(error?.message || 'Unknown error', 100),
        error_name: error?.name || 'UnknownError',
        user_id: userContext.userId || 'anonymous',
        page_url: window.location.pathname,
        timestamp: new Date().toISOString(),
        ...additionalData
      };

      // Include stack trace if available (truncated)
      if (error?.stack) {
        eventData.stack_trace = this.truncateString(error.stack, 500);
      }

      await logEvent(this.analytics, 'client_error', eventData);
    } catch (logError) {
      console.error('Failed to log error to Firebase Analytics:', logError);
    }
  }

  /**
   * Log critical error to Firestore (for important errors that need detailed tracking)
   */
  async logToFirestore(errorType, error, userContext = {}, additionalData = {}) {
    if (!this.db) {
      console.warn('Firestore not available for error logging');
      return;
    }

    // Check rate limiting
    if (this.shouldRateLimit(errorType)) {
      return;
    }

    // Prevent infinite loops - don't log auth/network errors to Firestore
    if (error?.message?.includes('auth/quota-exceeded') || 
        error?.message?.includes('Firebase: Error (auth/quota-exceeded)') ||
        errorType === 'network_error' ||
        errorType === 'authentication_error') {
      console.warn(`Skipping Firestore logging for ${errorType} to prevent loops`);
      return;
    }

    try {
      const errorDoc = {
        type: errorType,
        message: error?.message || 'Unknown error',
        name: error?.name || 'UnknownError',
        stack: error?.stack || null,
        userContext,
        environmentContext: this.getEnvironmentContext(),
        additionalData,
        timestamp: serverTimestamp(),
        resolved: false
      };

      await addDoc(collection(this.db, 'error_logs'), errorDoc);
    } catch (logError) {
      console.error('Failed to log error to Firestore:', logError);
      // Fallback to Analytics if Firestore fails
      await this.logToAnalytics(errorType, error, userContext, {
        ...additionalData,
        firestore_log_failed: true
      });
    }
  }

  /**
   * Log JavaScript errors (from window.onerror)
   */
  async logJavaScriptError(message, filename, lineno, colno, error, currentUser = null) {
    const userContext = this.getUserContext(currentUser);
    const additionalData = {
      filename,
      line_number: lineno,
      column_number: colno
    };

    // Always log to Analytics
    await this.logToAnalytics('javascript_error', 
      { message, name: 'JavaScriptError', stack: error?.stack }, 
      userContext, 
      additionalData
    );

    // Log critical errors to Firestore in production
    if (this.isProduction && this.shouldLogToFirestore(error)) {
      await this.logToFirestore('javascript_error', 
        { message, name: 'JavaScriptError', stack: error?.stack }, 
        userContext, 
        additionalData
      );
    }
  }

  /**
   * Log unhandled Promise rejections
   */
  async logPromiseRejection(reason, promise, currentUser = null) {
    const userContext = this.getUserContext(currentUser);
    const error = reason instanceof Error ? reason : new Error(String(reason));
    
    const additionalData = {
      rejection_type: typeof reason,
      promise_string: promise.toString().substring(0, 200)
    };

    // Always log to Analytics
    await this.logToAnalytics('promise_rejection', error, userContext, additionalData);

    // Log critical Promise rejections to Firestore in production
    if (this.isProduction && this.shouldLogToFirestore(error)) {
      await this.logToFirestore('promise_rejection', error, userContext, additionalData);
    }
  }

  /**
   * Log React component errors
   */
  async logReactError(error, errorInfo, currentUser = null) {
    const userContext = this.getUserContext(currentUser);
    const additionalData = {
      component_stack: this.truncateString(errorInfo?.componentStack || '', 1000)
    };

    // Always log to Analytics
    await this.logToAnalytics('react_error', error, userContext, additionalData);

    // Always log React errors to Firestore (they're typically critical)
    await this.logToFirestore('react_error', error, userContext, additionalData);
  }

  /**
   * Log custom application errors
   */
  async logCustomError(errorType, error, currentUser = null, additionalData = {}) {
    const userContext = this.getUserContext(currentUser);

    // Always log to Analytics
    await this.logToAnalytics(errorType, error, userContext, additionalData);

    // Log to Firestore if marked as critical or in production for important errors
    if (additionalData.critical || (this.isProduction && this.shouldLogToFirestore(error))) {
      await this.logToFirestore(errorType, error, userContext, additionalData);
    }
  }

  /**
   * Determine if error should be logged to Firestore (for detailed tracking)
   */
  shouldLogToFirestore(error) {
    // Log specific error types that are critical
    const criticalErrors = [
      'ChunkLoadError',
      'TypeError',
      'ReferenceError',
      'SecurityError',
      'NetworkError'
    ];

    return criticalErrors.includes(error?.name) || 
           (error?.message && error.message.includes('Loading chunk'));
  }

  /**
   * Truncate string to avoid Firebase parameter limits
   */
  truncateString(str, maxLength) {
    if (!str || str.length <= maxLength) return str;
    return str.substring(0, maxLength) + '...';
  }

  /**
   * Log browser performance issues
   */
  async logPerformanceIssue(metricName, value, currentUser = null) {
    const userContext = this.getUserContext(currentUser);
    
    await this.logToAnalytics('performance_issue', 
      { name: 'PerformanceIssue', message: `${metricName}: ${value}` }, 
      userContext, 
      { metric_name: metricName, metric_value: value }
    );
  }
}

export default ErrorLoggingService;